import type { StatsigUser } from '@statsig/client-core';
import { StatsigProvider } from '@statsig/react-bindings';
import type { StatsigEnvironment } from '@statsig/react-bindings';
import React, { useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { useCheckoutSessionV3 } from "@/modules/checkout-session";
import { getResolvedCheckoutId } from '@/lib/checkout-id';
import {
  getCheckoutUserIdCookie,
  UNDEFINED_CHECKOUT_ID,
  UNDEFINED_CHECKOUT_USER_ID,
} from '@/services/cookie';

import { STATSIG_CLIENT_KEY, STATSIG_GATE } from './constants';
import { getStatsigTierFromEnv } from './statsig-env';
import { StatsigReplayInitializer } from './statsig-replay-initializer';
import { getStatsigReplayPlugins } from './statsig-service';

export function getStatsigSdkKey(): string {
  return STATSIG_CLIENT_KEY;
}

export function getStatsigEnvironment(): StatsigEnvironment {
  const tier = getStatsigTierFromEnv(
    import.meta.env.VITE_APP_ENV,
    import.meta.env.MODE,
  );
  return { tier } as StatsigEnvironment;
}

export interface StatsigUserSessionFields {
  orderId?: string;
  merchantId?: string;
  customerId?: string;
  countryCode?: string;
  locale?: string;
}

const SESSION_TO_CUSTOM_IDS: ReadonlyArray<
  [keyof StatsigUserSessionFields, string]
> = [
  ['orderId', 'order_id'],
  ['merchantId', 'merchant_id'],
  ['customerId', 'customer_id'],
];

const SESSION_TO_CUSTOM: ReadonlyArray<
  [keyof StatsigUserSessionFields, string]
> = [
  ['countryCode', 'country_code'],
  ['locale', 'locale'],
];

function mapSessionFieldsToRecord(
  fields: StatsigUserSessionFields,
  mapping: ReadonlyArray<[keyof StatsigUserSessionFields, string]>,
): Record<string, string> {
  return mapping.reduce<Record<string, string>>((acc, [key, statsigKey]) => {
    const value = fields[key];
    if (value != null && value !== '') acc[statsigKey] = value;
    return acc;
  }, {});
}

export function buildStatsigUser(
  userID: string,
  checkoutId: string,
  session: StatsigUserSessionFields | null | undefined,
): StatsigUser {
  const customIDs: Record<string, string> = {};
  if (checkoutId && checkoutId !== UNDEFINED_CHECKOUT_ID) {
    customIDs.checkout_id = checkoutId;
  }
  if (session) {
    Object.assign(
      customIDs,
      mapSessionFieldsToRecord(session, SESSION_TO_CUSTOM_IDS),
    );
  }

  const custom: Record<string, string> = session
    ? mapSessionFieldsToRecord(session, SESSION_TO_CUSTOM)
    : {};

  return {
    userID: userID || UNDEFINED_CHECKOUT_USER_ID,
    ...(Object.keys(customIDs).length > 0 && { customIDs }),
    ...(Object.keys(custom).length > 0 && { custom }),
  };
}

interface StatsigProviderWithSessionProps {
  children: ReactNode;
  loading?: ReactNode;
}

interface CheckoutSession {
  order?: { id?: string; countryCode?: string };
  merchant?: { id?: string };
  customerId?: string;
  locale?: string | null;
}

function sessionToStatsigFields(
  session: CheckoutSession,
): StatsigUserSessionFields {
  return {
    orderId: session.order?.id,
    merchantId: session.merchant?.id,
    customerId: session.customerId,
    countryCode: session.order?.countryCode,
    locale: session.locale ?? undefined,
  };
}

/** Handshake runs before login; do not block the tree on checkout/v3. */
function isHandshakePath(
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): boolean {
  return /\/handshake(\/|$)/.test(pathname);
}

export function StatsigProviderWithSession({
  children,
  loading = null,
}: StatsigProviderWithSessionProps): ReactElement {
  const skipSessionForHandshake = isHandshakePath();
  const { data: session } = useCheckoutSessionV3({
    skip: skipSessionForHandshake,
  });
  const userID = getCheckoutUserIdCookie() || UNDEFINED_CHECKOUT_USER_ID;
  const checkoutId = getResolvedCheckoutId() ?? UNDEFINED_CHECKOUT_ID;

  const user = useMemo(
    () =>
      buildStatsigUser(
        userID,
        checkoutId,
        session ? sessionToStatsigFields(session) : undefined,
      ),
    [userID, checkoutId, session],
  );

  const sdkKey = getStatsigSdkKey();
  const options = useMemo(
    () => ({
      environment: getStatsigEnvironment(),
      plugins: getStatsigReplayPlugins(),
    }),
    [],
  );

  // Handshake must paint/route without waiting for an unauthenticated session fetch.
  if (!skipSessionForHandshake && session === undefined) {
    return React.createElement(React.Fragment, null, loading);
  }

  return React.createElement(StatsigProvider, {
    sdkKey,
    user,
    options,
    children: [
      React.createElement(StatsigReplayInitializer, {
        key: 'statsig-session-replay-init',
        sdkKey,
        samplingGateName: STATSIG_GATE.ENABLE_SESSION_REPLAY,
      }),
      children,
    ],
  });
}
